import { useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Send } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";
import { BRAND, SERVICES } from "../data/siteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INFO = [
  { icon: Phone, label: "Call or Text", value: BRAND.phoneDisplay, href: `tel:${BRAND.phone}`, testid: "contact-phone-card" },
  { icon: Mail, label: "Email", value: BRAND.email, href: `mailto:${BRAND.email}`, testid: "contact-email-card" },
  { icon: MapPin, label: "Service Area", value: BRAND.location, href: null, testid: "contact-area-card" },
  { icon: Clock, label: "Hours", value: "Mon–Sat · 8am–6pm", href: null, testid: "contact-hours-card" },
];

const inputCls =
  "w-full border border-[#234E33]/20 bg-[#FAF7F2] px-4 py-3 text-sm text-[#1A2E20] placeholder-[#4a544c]/50 outline-none transition-colors focus:border-[#2E6941]";

export default function Contact() {
  const location = useLocation();
  const pre = location.state || {};
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: pre.service || "General enquiry",
    property_type: "",
    preferred_date: "",
    message: pre.message || "",
  });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Enquiry sent — Chris will get back to you within one business day.");
      setForm({ name: "", email: "", phone: "", service: "General enquiry", property_type: "", preferred_date: "", message: "" });
    } catch (err) {
      toast.error(`Couldn't send right now — please call ${BRAND.phoneDisplay} instead.`);
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="bg-[#FBF9F5] pt-28 lg:pt-36">
      <section className="mx-auto max-w-7xl px-5 pb-20 lg:px-8 lg:pb-28">
        <Reveal>
          <Eyebrow>Consultation & Quotes</Eyebrow>
          <h1 className="mt-4 max-w-3xl font-display text-4xl font-black tracking-tight text-[#1A2E20] sm:text-5xl lg:text-6xl">
            Let's walk your property.
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-[#4a544c] sm:text-lg">
            Tell us what you're dreaming up. Every enquiry lands directly with Chris — most quotes come back within
            one business day.
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-10 lg:grid-cols-5">
          <div className="space-y-4 lg:col-span-2">
            {INFO.map((c, i) => (
              <Reveal key={c.label} delay={i * 0.07}>
                <div className="flex items-center gap-4 border border-[#234E33]/12 bg-[#FAF7F2] p-5" data-testid={c.testid}>
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#2E6941]/10 text-[#2E6941]">
                    <c.icon size={18} />
                  </span>
                  <div>
                    <p className="font-mono-x text-[10px] font-semibold uppercase tracking-[0.25em] text-[#C86D51]">{c.label}</p>
                    {c.href ? (
                      <a href={c.href} className="text-sm font-semibold text-[#1A2E20] transition-colors hover:text-[#2E6941]">
                        {c.value}
                      </a>
                    ) : (
                      <p className="text-sm font-semibold text-[#1A2E20]">{c.value}</p>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.3}>
              <div className="bg-[#1A2E20] p-6 text-[#FBF9F5]">
                <p className="font-display text-lg font-bold">Skip the form — book a visit.</p>
                <p className="mt-2 text-sm text-[#FBF9F5]/65">
                  Pick a time on our calendar and Chris walks your property for free.
                </p>
                <a
                  href="/booking"
                  data-testid="contact-booking-link"
                  className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#72B043] px-5 py-2.5 text-sm font-bold text-[#0C0E0D] transition-colors hover:bg-[#FBF9F5]"
                >
                  Book a Free Visit
                </a>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15} className="lg:col-span-3">
            <form
              onSubmit={submit}
              data-testid="contact-enquiry-form"
              className="border border-[#234E33]/12 bg-[#FAF7F2] p-7 lg:p-10"
            >
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className="mb-2 block font-mono-x text-[10px] font-semibold uppercase tracking-[0.25em] text-[#2E6941]">Name *</label>
                  <input id="contact-name" required value={form.name} onChange={set("name")} placeholder="Jane Doe" data-testid="contact-name-input" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="contact-email" className="mb-2 block font-mono-x text-[10px] font-semibold uppercase tracking-[0.25em] text-[#2E6941]">Email *</label>
                  <input id="contact-email" type="email" required value={form.email} onChange={set("email")} placeholder="jane@email.com" data-testid="contact-email-input" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="contact-phone" className="mb-2 block font-mono-x text-[10px] font-semibold uppercase tracking-[0.25em] text-[#2E6941]">Phone</label>
                  <input id="contact-phone" value={form.phone} onChange={set("phone")} placeholder="(___) ___-____" data-testid="contact-phone-input" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="contact-service" className="mb-2 block font-mono-x text-[10px] font-semibold uppercase tracking-[0.25em] text-[#2E6941]">Service</label>
                  <select id="contact-service" value={form.service} onChange={set("service")} data-testid="contact-service-select" className={inputCls}>
                    <option>General enquiry</option>
                    {SERVICES.map((s) => (
                      <option key={s.id}>{s.title}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="contact-property" className="mb-2 block font-mono-x text-[10px] font-semibold uppercase tracking-[0.25em] text-[#2E6941]">Property Type</label>
                  <select id="contact-property" value={form.property_type} onChange={set("property_type")} data-testid="contact-property-select" className={inputCls}>
                    <option value="">Select…</option>
                    <option>Townhouse / small lot</option>
                    <option>Standard residential lot</option>
                    <option>Large estate</option>
                    <option>Commercial property</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="contact-date" className="mb-2 block font-mono-x text-[10px] font-semibold uppercase tracking-[0.25em] text-[#2E6941]">Preferred Date</label>
                  <input id="contact-date" type="date" value={form.preferred_date} onChange={set("preferred_date")} data-testid="contact-date-input" className={inputCls} />
                </div>
              </div>
              <div className="mt-5">
                <label htmlFor="contact-message" className="mb-2 block font-mono-x text-[10px] font-semibold uppercase tracking-[0.25em] text-[#2E6941]">Tell us about the project *</label>
                <textarea id="contact-message" required rows={5} value={form.message} onChange={set("message")} placeholder="Size of the yard, what you'd like done, timing…" data-testid="contact-message-input" className={inputCls} />
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-btn"
                className="group mt-7 inline-flex items-center gap-2 rounded-full bg-[#1A2E20] px-8 py-4 text-sm font-semibold text-[#FBF9F5] transition-colors duration-200 hover:bg-[#2E6941] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {sending ? "Sending…" : "Send Enquiry"}
                <Send size={15} className="transition-transform duration-200 group-hover:translate-x-1" />
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
